import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import {
  RentCollectionService,
  Payment,
  PaymentMethodOption,
  PaymentMethodKey,
} from 'src/shared/services/rent-collection.service';

// Precomputed per-method display strings — templates never call functions (CLAUDE.md rule).
export interface PayMethodView {
  key: PaymentMethodKey;
  label: string;
  subLabel: string;
  fee: number;
  total: number;
  totalLabel: string;
}

@Component({
  templateUrl: 'pay-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PayPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private featureGate = inject(FeatureGateService);
  private rentService = inject(RentCollectionService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  loaded = false;

  payment: Payment;
  methodViews: PayMethodView[] = [];
  selectedKey: PaymentMethodKey;
  selectedFeeLabel = '';
  selectedTotalLabel = '';

  paid = false;

  payAction = () => this.pay();
  doneAction = () => this.done();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.payment = this.navCtrl.getParam('payment') || params?.payment;
    const methods = (await this.rentService.getPaymentMethods()).filter(m => m.enabled);
    this.methodViews = methods.map(method => this.buildMethodView(method));
    this.selectMethod(this.methodViews[0]?.key);
    this.paid = this.payment?.status === 'Paid';
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Payment page');
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  buildMethodView(method: PaymentMethodOption): PayMethodView {
    const fee = this.payment ? this.rentService.estimateFee(this.payment.amount, method.key) : 0;
    const tenantPaysFee = method.fee_paid_by === 'tenant';
    const total = (this.payment?.amount || 0) + (tenantPaysFee ? fee : 0);
    return {
      key: method.key,
      label: method.label,
      subLabel: method.rate + (tenantPaysFee ? '' : ' · fee covered by owner'),
      fee: tenantPaysFee ? fee : 0,
      total,
      totalLabel: this.moneyLabel(total),
    };
  }

  moneyLabel(amount: number): string {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  selectMethod(key: PaymentMethodKey): void {
    this.selectedKey = key;
    const view = this.methodViews.find(v => v.key === key);
    this.selectedFeeLabel = view && view.fee > 0 ? `$${  this.moneyLabel(view.fee)}` : '';
    this.selectedTotalLabel = view ? view.totalLabel : this.moneyLabel(this.payment?.amount || 0);
    this.cdr.markForCheck();
  }

  async pay(): Promise<void> {
    this.featureGate.gate();
  }

  done(): void {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('finance');
    }
  }
}
